import React, { useEffect, useRef, useState } from 'react';

const HeatmapOverlay = ({ heatmapSrc, threshold = 0, className }) => {
    const canvasRef = useRef(null);
    const originalDataRef = useRef(null); 
    const [imageLoaded, setImageLoaded] = useState(false);

    // 1. Load the High-Res Base64 image and extract raw pixel data ONCE into memory
    useEffect(() => {
        if (!heatmapSrc || !canvasRef.current) return;

        const img = new Image();
        img.onload = () => {
            const canvas = canvasRef.current;
            // willReadFrequently optimizes the canvas for rapid slider dragging
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            
            // Match the exact high-resolution dimensions of the original image
            canvas.width = img.width;
            canvas.height = img.height;
            
            ctx.drawImage(img, 0, 0);
            
            // Cache the original untouched pixel data
            originalDataRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
            setImageLoaded(true);
        };
        img.src = heatmapSrc;
    }, [heatmapSrc]);

    // 2. Apply the threshold dynamically on slider movement
    useEffect(() => {
        if (!imageLoaded || !originalDataRef.current || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        // Clone the original data so we don't permanently destroy pixels
        const imageData = new ImageData(
            new Uint8ClampedArray(originalDataRef.current.data), 
            canvas.width, 
            canvas.height
        );
        const data = imageData.data;

        const thresholdVal = threshold / 100.0;
        const fadeZone = 0.05; // 5% fade zone to keep edges looking smooth and anti-aliased

        for (let i = 0; i < data.length; i += 4) {
            const originalAlpha = originalDataRef.current.data[i + 3];
            
            if (originalAlpha === 0) continue; // Skip empty space

            // Reverse-engineer the backend's intensity value from the alpha channel
            // Backend formula was: alpha = (val * 155) + 75
            let val = (originalAlpha - 75) / 155.0;
            
            // Clamp between 0 and 1 to handle any slight image compression artifacts
            val = Math.max(0, Math.min(1, val));

            // Apply the threshold filter
            if (val <= thresholdVal - fadeZone) {
                data[i + 3] = 0; // Fully hide pixel
            } else if (val < thresholdVal) {
                // Smooth fade for pixels on the borderline
                const fadeRatio = (val - (thresholdVal - fadeZone)) / fadeZone;
                data[i + 3] = originalAlpha * fadeRatio;
            }
            // Pixels above the threshold keep their exact original color and alpha
        }

        ctx.putImageData(imageData, 0, 0);

    }, [imageLoaded, threshold]);

    return (
        <canvas
            ref={canvasRef}
            className={`heatmap-overlay ${className || ''}`}
            style={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                pointerEvents: 'none',
                objectFit: 'contain'
            }}
        />
    );
};

export default HeatmapOverlay;